import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatMoney } from '@/lib/money';
import { Skeleton } from '@/components/ui/skeleton';
import { TableCard } from '@/components/ui/table-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePromotionCodeAnalytics } from '@/lib/api/promotionAnalytics';

import type { PromotionAnalyticsRow, PromotionCodeAnalyticsRow } from '../types';

interface Props {
  storeId: string;
  promotions: PromotionAnalyticsRow[];
  currency: string;
  startDate: Date;
  endDate: Date;
  isLoading: boolean;
  isError: boolean;
}

const COLUMN_COUNT = 5;

type SortKey = 'redemptions' | 'discountedSales' | 'netSales' | 'discountGiven';
type SortDir = 'asc' | 'desc';
type SortState = { key: SortKey; dir: SortDir } | null;

export function PromotionAnalyticsTable({
  storeId,
  promotions,
  currency,
  startDate,
  endDate,
  isLoading,
  isError,
}: Props) {
  const { t } = useTranslation('analytics');
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<SortState>(null);

  const money = (v: number) => (currency ? formatMoney(v, currency) : '—');

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  const sortedPromotions = React.useMemo(() => {
    if (!sort) return promotions;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...promotions].sort((a, b) => (a[sort.key] - b[sort.key]) * dir);
  }, [promotions, sort]);

  return (
    <TableCard>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('promotionAnalytics.table.promotion')}</TableHead>
            <SortableHead
              label={t('promotionAnalytics.table.redemptions')}
              sortKey="redemptions"
              sort={sort}
              onToggle={toggleSort}
            />
            <SortableHead
              label={t('promotionAnalytics.table.discountedSales')}
              sortKey="discountedSales"
              sort={sort}
              onToggle={toggleSort}
            />
            <SortableHead
              label={t('promotionAnalytics.table.netSales')}
              sortKey="netSales"
              sort={sort}
              onToggle={toggleSort}
            />
            <SortableHead
              label={t('promotionAnalytics.table.discountGiven')}
              sortKey="discountGiven"
              sort={sort}
              onToggle={toggleSort}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={COLUMN_COUNT}>
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              </TableRow>
            ))
          ) : isError ? (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground py-10 text-center text-sm">
                {t('promotionAnalytics.error')}
              </TableCell>
            </TableRow>
          ) : promotions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground py-10 text-center text-sm">
                {t('promotionAnalytics.empty')}
              </TableCell>
            </TableRow>
          ) : (
            sortedPromotions.map((promo) => {
              const isExpanded = expandedId === promo.promotionId;
              return (
                <React.Fragment key={promo.promotionId}>
                  <TableRow
                    className="cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : promo.promotionId)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ChevronRight
                          className={cn(
                            'text-muted-foreground size-4 shrink-0 transition-transform',
                            isExpanded && 'rotate-90',
                          )}
                        />
                        <span className="font-medium">{promo.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {promo.redemptions.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{money(promo.discountedSales)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(promo.netSales)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(promo.discountGiven)}</TableCell>
                  </TableRow>
                  {isExpanded && (
                    <PromotionCodeRows
                      storeId={storeId}
                      promotionId={promo.promotionId}
                      currency={currency}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  )}
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableCard>
  );
}

function SortableHead({
  label,
  sortKey,
  sort,
  onToggle,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onToggle: (key: SortKey) => void;
}) {
  const active = sort?.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead className="text-right">
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={cn(
          'hover:text-foreground inline-flex items-center gap-1',
          active ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
        <Icon className={cn('size-3', active ? 'opacity-100' : 'opacity-30')} />
      </button>
    </TableHead>
  );
}

interface CodeRowsProps {
  storeId: string;
  promotionId: string;
  currency: string;
  startDate: Date;
  endDate: Date;
}

function PromotionCodeRows({ storeId, promotionId, currency, startDate, endDate }: CodeRowsProps) {
  const { t } = useTranslation('analytics');
  const { data, isLoading } = usePromotionCodeAnalytics({
    storeId,
    promotionId,
    startDate,
    endDate,
    currency: currency || undefined,
  });

  const money = (v: number) => (currency ? formatMoney(v, currency) : '—');

  if (isLoading) {
    return (
      <TableRow className="bg-muted/20 hover:bg-muted/20">
        <TableCell colSpan={COLUMN_COUNT} className="pl-12">
          <Skeleton className="h-5 w-1/2" />
        </TableCell>
      </TableRow>
    );
  }

  const codes: PromotionCodeAnalyticsRow[] = data?.codes ?? [];

  if (codes.length === 0) {
    return (
      <TableRow className="bg-muted/20 hover:bg-muted/20">
        <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground pl-12 text-sm">
          {t('promotionAnalytics.noCodes')}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {codes.map((code) => (
        <TableRow key={code.codeId} className="bg-muted/20 hover:bg-muted/20">
          <TableCell className="pl-12 font-mono text-sm">{code.code}</TableCell>
          <TableCell className="text-right text-sm tabular-nums">
            {code.redemptions.toLocaleString()}
          </TableCell>
          <TableCell className="text-right text-sm tabular-nums">{money(code.discountedSales)}</TableCell>
          <TableCell className="text-right text-sm tabular-nums">{money(code.netSales)}</TableCell>
          <TableCell className="text-right text-sm tabular-nums">{money(code.discountGiven)}</TableCell>
        </TableRow>
      ))}
    </>
  );
}
